import React, { useState, useEffect } from 'react'
import { XMasonry, XBlock } from 'react-xmasonry'
import { IGatsbyImageData } from 'gatsby-plugin-image'
import FirmJobTypes from './firm/jobTypes'
import MainMedia from './firm/mainMedia'
import FirmMap from './firm/map'
import FirmEssentials from './firm/essentials'
import Description from '../ui/description'
import Wrapper from '../ui/wrapper'
import FirmTools from './firm/tools'
import FirmEcoSystem from './firm/ecosystem'
import { EcoSystem, Firm, JobType, Tool, mediaType } from './firm/mapper'
import { FirmData, formatFirmDataForWrappers } from './format'
import PremiumSkeleton from '../ui/skeleton/premiumSkeleton'
import BadgesCard from './firm/badges/badgesCard'
import { OfficesAddressesCard } from './firm/OfficesAddressesCard'
import { GroupWithLogo } from '../../types/Groupement'
import FirmGroupsList from '../groups/FirmGroupsList'
import { MediaInterface, ModalWithMedias } from '../ui/medias/ModalWithMedia'
import { ApplyCard } from './firm/ApplyCard'
import { FirmCTA } from './FirmCTA'

type PremiumTemplateProps = {
  firm: Firm
  groups?: GroupWithLogo[]
  googleMapsApiKey: string
  youTubeApiKey: string | null
  color: string | null
}

const PremiumTemplate: React.FC<PremiumTemplateProps> = ({
  firm,
  googleMapsApiKey,
  youTubeApiKey,
  color,
  groups = [],
}) => {
  const [isDataFormated, setIsDataFormated] = useState(false)
  const [firmData, setFirmData] = useState<FirmData | null>(null)
  const [focusedAddress, setFocusedAddress] = useState<{
    street: string
    city: string
    postalCode: string
  } | null>(null)

  useEffect(() => {
    if (firm) {
      setFirmData(formatFirmDataForWrappers(firm))
      setIsDataFormated(true)
    }
  }, [firm])

  return (
    <div>
      {!isDataFormated ? (
        <PremiumSkeleton />
      ) : (
        <XMasonry maxColumns={4} updateOnImagesLoad responsive smartUpdate>
          <XBlock width={2}>
            <div className="masonry-block motion-safe:animate-enter">
              <MainMedia
                mainMedia={firm?.mainMedia}
                name={firm?.name}
                youTubeApiKey={youTubeApiKey}
                otherMedias={
                  [
                    firm?.mainMedia,
                    ...(firm?.medias as (MediaInterface & {
                      id: string
                    })[]),
                  ] as (MediaInterface & {
                    id: string
                  })[]
                }
              />
            </div>
          </XBlock>
          {firm?.description && (
            <XBlock width={2}>
              <div className="masonry-block motion-safe:animate-enter">
                <Description
                  data={firm.description as string}
                  label="Qui sommes nous ?"
                />
              </div>
            </XBlock>
          )}
          <XBlock width={1}>
            <div className="masonry-block motion-safe:animate-enter">
              <FirmMap
                offices={firm.offices}
                googleMapsApiKey={googleMapsApiKey}
                focusedAddress={focusedAddress}
              />
            </div>
          </XBlock>
          <XBlock width={1}>
            <div className="masonry-block motion-safe:animate-enter">
              <OfficesAddressesCard
                offices={firm.offices || []}
                setFocusedAddress={setFocusedAddress}
                additionalClasses="max-h-[396px]"
              />
            </div>
          </XBlock>
          {firm.badges && (
            <XBlock width={1}>
              <div className="masonry-block motion-safe:animate-enter">
                <BadgesCard badges={firm.badges} color={color || 'primary'} />
              </div>
            </XBlock>
          )}
          {groups && groups?.length > 0 && (
            <XBlock width={1}>
              <div className="masonry-block motion-safe:animate-enter">
                <Wrapper>
                  <FirmGroupsList groups={groups} />
                </Wrapper>
              </div>
            </XBlock>
          )}
          <XBlock width={1}>
            <div className="masonry-block motion-safe:animate-enter">
              <FirmEssentials firm={firm} color={color || null} />
            </div>
          </XBlock>
          <XBlock width={1}>
            <div className="masonry-block motion-safe:animate-enter">
              <ApplyCard firm={firm} color={color || null} />
            </div>
          </XBlock>
          {firmData &&
            firmData?.length > 0 &&
            firmData.map((data) => {
              switch (data?.type) {
                case 'tools':
                  return (
                    <XBlock width={1} key={data?.index}>
                      <div className="masonry-block motion-safe:animate-enter">
                        <Wrapper>
                          <FirmTools tools={data.data as Tool[]} />
                        </Wrapper>
                      </div>
                    </XBlock>
                  )

                case 'jobTypes':
                  return (
                    <XBlock width={1} key={data?.index}>
                      <div className="masonry-block motion-safe:animate-enter">
                        <Wrapper>
                          <FirmJobTypes jobTypes={data.data as JobType[]} />
                        </Wrapper>
                      </div>
                    </XBlock>
                  )

                case 'ecoSystems':
                  return (
                    data?.data?.length > 0 && (
                      <XBlock width={1} key={data?.index}>
                        <div className="masonry-block motion-safe:animate-enter">
                          <Wrapper>
                            <FirmEcoSystem
                              ecoSystems={data?.data as EcoSystem[]}
                            />
                          </Wrapper>
                        </div>
                      </XBlock>
                    )
                  )

                case 'firmValues':
                  return (
                    <XBlock width={1} key={data?.index}>
                      <div className="masonry-block motion-safe:animate-enter">
                        <Description
                          data={data.data as string}
                          label="Nos valeurs"
                        />
                      </div>
                    </XBlock>
                  )

                case 'clientDescription':
                  return (
                    <XBlock width={1} key={data?.index}>
                      <div className="masonry-block motion-safe:animate-enter">
                        <Description
                          data={data.data as string}
                          label="Nos clients"
                        />
                      </div>
                    </XBlock>
                  )

                case 'image':
                  return (
                    <XBlock width={1} key={data?.index}>
                      <div className="masonry-block motion-safe:animate-enter">
                        <ModalWithMedias
                          id={data.data?.id as string}
                          key={`media-${data.index}`}
                          media={
                            (data.data as mediaType)?.media as
                              | string
                              | IGatsbyImageData
                              | null
                          }
                          name={firm.name ?? ''}
                          otherMedias={
                            firm.medias as (MediaInterface & {
                              id: string
                            })[]
                          }
                        />
                      </div>
                    </XBlock>
                  )

                case 'video':
                  return (
                    <XBlock width={1} key={data?.index}>
                      <div className="masonry-block motion-safe:animate-enter">
                        <ModalWithMedias
                          id={data.data?.id as string}
                          media={
                            (data.data as mediaType)?.media as
                              | string
                              | IGatsbyImageData
                              | null
                          }
                          name={firm.name ?? ''}
                          video
                          youTubeApiKey={youTubeApiKey}
                        />
                      </div>
                    </XBlock>
                  )

                default:
                  return null
              }
            })}
        </XMasonry>
      )}
      <div className="p-2">
        <FirmCTA
          data={firm}
          color={color || undefined}
          title={`Envie de rejoindre ${firm.name} ?`}
          buttonLabel="Postuler"
        />
      </div>
    </div>
  )
}

export default PremiumTemplate
